import React, { useState } from "react";
import { Pencil, RefreshCw, Share2, CreditCard, CalendarClock } from "lucide-react";
import IncentiveHistoryModal from "./IncentiveHistoryModal"; 
import PaymentDetailModal from "./Paymentdetailsmodal";
import Paymentwithdraw from "./Paymentwithdraw";
import referralCardIcon from "../assets/referralCardIcon.png";
import transferIcon from "../assets/transferIcon.png";
import payoutIcon from "../assets/payoutIcon.png";
import reschedulingIcon from "../assets/reschedulingIcon.png";

const ACTIONS = [
  { key: "incentive", icon: transferIcon, label: "Incentive History" },
  { key: "referral", icon: referralCardIcon, label: "Referral History" },
  { key: "payout", icon: payoutIcon, label: "Payout History" },
  { key: "reschedule", icon: reschedulingIcon, label: "Reschedule" },
];

/**
 * PaymentModeCard
 * Small self-contained card showing the worker's payment mode (editable)
 * plus a row of quick-action icon buttons, each with a hover tooltip.
 *
 * Props:
 * - workerId: string | number — needed to open the Incentive History modal
 * - paymentMode: string, e.g. "Daily"
 * - onEditPaymentMode: () => void
 * - onIncentiveHistory: () => void — optional, still called (in addition to
 *     opening the modal) if you want to track/log the click elsewhere
 * - onReferralHistory: () => void
 * - onPayoutHistory: () => void
 * - onSchedule: () => void
 */
const PaymentModeCard = ({
  workerId,
  paymentMode = "Daily",
  onEditPaymentMode,
  onIncentiveHistory,
  onReferralHistory,
  onPayoutHistory,
  onSchedule,
  isEditFeaturesEnabled = true, // controls Payment Mode edit + Reschedule; false = "coming soon"
}) => {
  const [hoveredKey, setHoveredKey] = useState(null);
  const [isIncentiveModalOpen, setIsIncentiveModalOpen] = useState(false);
  const [ispayouthistorymodalopen, setispayouthistorymodalopen] = useState(false);
  const [showpayoutscheduledmodal, setshowpayoutscheduledmodal] = useState(false);
  const [isEditHovered, setIsEditHovered] = useState(false);

  const handlers = {
    incentive: () => {
      setIsIncentiveModalOpen(true);
      onIncentiveHistory?.();
    },
    referral: onReferralHistory,
    payout: () => {
      setispayouthistorymodalopen(true);
      onPayoutHistory?.();
    },
    reschedule: () => {
      if (!isEditFeaturesEnabled) return;
      setshowpayoutscheduledmodal(true);
      onSchedule?.();
    },
  };

  return (
    <>
      <div className="bg-[#F6FBFF] rounded-2xl shadow-sm border border-gray-200 p-5 flex items-center justify-between gap-2 min-w-0">
        {/* Payment mode label + edit */}
        <div className="flex flex-col gap-1 min-w-0 flex-shrink-0">
          <span className="text-[11px] text-gray-500 font-medium whitespace-nowrap">Payment Mode</span>
          <div className="flex items-center gap-1.5 text-base font-bold text-[#101d75] whitespace-nowrap">
            {paymentMode}
            <div
              className="relative"
              onMouseEnter={() => setIsEditHovered(true)}
              onMouseLeave={() => setIsEditHovered(false)}
            >
              <Pencil
                className={`h-3.5 w-3.5 text-[#101d75] flex-shrink-0 ${
                  isEditFeaturesEnabled ? "cursor-pointer" : "cursor-not-allowed opacity-50"
                }`}
                onClick={() => {
                  if (!isEditFeaturesEnabled) return;
                  onEditPaymentMode();
                }}
              />
              {isEditHovered && (
                <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 z-50 whitespace-nowrap rounded-md bg-gray-900 text-white text-[11px] font-medium px-2 py-1 shadow-lg pointer-events-none">
                  {isEditFeaturesEnabled ? "Edit Payment Mode" : "Edit Payment Mode (coming soon)"}
                  <div className="absolute left-1/2 -translate-x-1/2 top-full w-0 h-0 border-4 border-transparent border-t-gray-900" />
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Quick action icons */}
        <div className="flex items-center gap-1.5 flex-shrink-0">
          {ACTIONS.map(({ key, icon: Icon, label }) => (
            <div
              key={key}
              className="relative"
              onMouseEnter={() => setHoveredKey(key)}
              onMouseLeave={() => setHoveredKey(null)}
            >
              <button
                type="button"
                onClick={handlers[key]}
                className={`p-1.5 text-[#101d75] flex-shrink-0 hover:text-white transition-colors ${
                  key === "reschedule" && !isEditFeaturesEnabled
                    ? "opacity-50 cursor-not-allowed"
                    : ""
                }`}
              >
                <img
                  src={Icon}
                  alt={label}
                  className="h-6 w-6"
                />
              </button>

              {hoveredKey === key && (
                <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 z-50 whitespace-nowrap rounded-md bg-gray-900 text-white text-[11px] font-medium px-2 py-1 shadow-lg pointer-events-none">
                  {key === "reschedule" && !isEditFeaturesEnabled
                    ? "Reschedule (coming soon)"
                    : label}
                  <div className="absolute left-1/2 -translate-x-1/2 top-full w-0 h-0 border-4 border-transparent border-t-gray-900" />
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      <IncentiveHistoryModal
        isOpen={isIncentiveModalOpen}
        onClose={() => setIsIncentiveModalOpen(false)}
        workerId={workerId}
      />

      <PaymentDetailModal
        isOpen={ispayouthistorymodalopen}
        onClose={() => setispayouthistorymodalopen(false)}
        workerId={workerId}
      />
      <Paymentwithdraw
        isOpen={showpayoutscheduledmodal}
        onClose={() => setshowpayoutscheduledmodal(false)}
        workerId={workerId}
      />
    </>
  );
};

export default PaymentModeCard;